import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { format, subDays } from 'date-fns'
import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'
import {
  LineChart,
} from 'echarts/charts'
import {
  GridComponent,
  ToolboxComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  DataZoomInsideComponent,
  DataZoomSliderComponent,
  TransformComponent,
  DatasetComponent,
} from 'echarts/components'
import {
  CanvasRenderer,
} from 'echarts/renderers'
import CenteredProgress from '../../../../utils/centeredprogress'
import {
	useFetchPlantByKey
} from '@graniot/common/adapters/plants'
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import Button from '@material-ui/core/Button'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import CircularProgress from '@material-ui/core/CircularProgress'
import HistoryIcon from '@material-ui/icons/History'
import { useTranslation } from 'react-i18next'

const INITIAL_LOOKBACK_DAYS = 200
const LOAD_MORE_DAYS = 200

const buildDateRange = (endDate = new Date(), lookbackDays = INITIAL_LOOKBACK_DAYS) => {
    const start = subDays(endDate, lookbackDays)

    return {
        fromDate: format(start, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
    }
}

const sortByDateAsc = (entries = []) => {
    return entries
        .filter((entry) => entry?.date)
        .slice()
        .sort((a, b) => new Date(a.date) - new Date(b.date))
}

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
    },
    loadMore: {
        marginLeft: 8,
        borderRadius: 8,
        padding: '5px 14px',
        fontSize: '0.8rem',
        fontWeight: 600,
        textTransform: 'none',
        whiteSpace: 'nowrap',
        boxShadow: 'none',
        '&:hover': { boxShadow: 'none' },
    },
}))

echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    LineChart,
    CanvasRenderer,
    DatasetComponent,
    ToolboxComponent,
    LegendComponent,
    DataZoomInsideComponent,
    DataZoomSliderComponent,
    TransformComponent,
  ])

export const BasicStatsParcel = ({ basicStats, setFeatureData, selectedLayer, isLoadingPlants }) => {
    
    const { t } = useTranslation();

    const [plotData, setPlotData] = React.useState();
    const [mergedLayerInfo, setMergedLayerInfo] = React.useState([]);
    const [dateRange, setDateRange] = React.useState(() => buildDateRange())

    const {
		isLoading: isLoadingPlant,
        isFetching: isFetchingPlant,
		data: dataPlant,
		refetch: refetchPlant,
	} = useFetchPlantByKey(
		basicStats.properties.parcel,
		basicStats.properties.key,
        selectedLayer.key,
        dateRange.fromDate,
        dateRange.endDate
	)	

    const responseLayerInfo = dataPlant?.features?.[0]?.properties?.info?.[selectedLayer.id] || []
    const sortedMergedLayerInfo = React.useMemo(() => sortByDateAsc(mergedLayerInfo), [mergedLayerInfo])
    const oldestInfoDate = sortedMergedLayerInfo?.[0]?.date
    const canLoadMore = Boolean(oldestInfoDate)

    React.useEffect(() => {
        setMergedLayerInfo([])
        setPlotData()
        setDateRange(buildDateRange())
    }, [basicStats?.properties?.key, selectedLayer?.id])

    React.useEffect(() => {
        if (!responseLayerInfo?.length) {
            return
        }

        setMergedLayerInfo((prevInfo) => {
            const normalizedResponse = sortByDateAsc(responseLayerInfo)
            if (!prevInfo?.length) {
                return normalizedResponse
            }

            const existingDates = new Set(prevInfo.map((item) => item.date))
            const newEntries = normalizedResponse.filter((item) => !existingDates.has(item.date))

            if (!newEntries.length) {
                return prevInfo
            }

            const merged = [...prevInfo, ...newEntries]
            merged.sort((a, b) => new Date(a.date) - new Date(b.date))
            return merged
        })
    }, [responseLayerInfo])

    React.useEffect(() => {
        if (sortedMergedLayerInfo?.length) {
            const graphData = sortedMergedLayerInfo
            setPlotData(formatData(graphData))
        } else if (!isLoadingPlant && dataPlant && !responseLayerInfo?.length) {
            setPlotData()
            refetchPlant()
        } else if (!dataPlant) {
            setPlotData()
        }
    }, [sortedMergedLayerInfo, isLoadingPlant, dataPlant, responseLayerInfo, refetchPlant])

    const handleLoadMore = React.useCallback(() => {
        if (!oldestInfoDate) {
            return
        }

        const firstDateObj = new Date(oldestInfoDate)
        if (isNaN(firstDateObj)) {
            return
        }

        const newFromDate = format(subDays(firstDateObj, LOAD_MORE_DAYS), 'yyyy-MM-dd')
        const newEndDate = format(subDays(firstDateObj, 1), 'yyyy-MM-dd')
        setDateRange((prevRange) => {
            if (newFromDate >= prevRange.fromDate) {
                return prevRange
            }

            return {
                fromDate: newFromDate,
                endDate: newEndDate,
            }
        })
    }, [oldestInfoDate])


    const formatData = (graphData) => {
        const dates = graphData.map((item) => format(new Date(item.date), 'd MMM yyyy'));

        const safeToFixed = (value, decimals = 3) => {
            if (value == null) return null;  // handles both null and undefined
            const num = typeof value === 'number' ? value : parseFloat(value);
            return isNaN(num) ? null : parseFloat(num.toFixed(decimals));
        };

        const maxValues = graphData.map((item) => safeToFixed(item.stats?.max));
        const minValues = graphData.map((item) => safeToFixed(item.stats?.min));
        const meanValues = graphData.map((item) => safeToFixed(item.stats?.mean));

        return {
          dates,
          maxValues,
          minValues,
          meanValues,
        };
    };
    const eChartsRef = React.useRef(null)

    const classes = useStyles()

    return (
        <React.Fragment>
            <List>
                <ListItem style={{ display: 'flex', alignItems: 'center' }}>
                    <ListItemIcon style={{ cursor: 'pointer' }}>
                        <ArrowBackIcon onClick={() => setFeatureData(null)} />
                    </ListItemIcon>
                    <ListItemText primary={`Plant ${basicStats.id}`} />
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleLoadMore}
                        disabled={!canLoadMore || isLoadingPlant || isFetchingPlant}
                        startIcon={
                            isFetchingPlant ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <HistoryIcon />
                            )
                        }
                        className={classes.loadMore}
                    >
                        {isFetchingPlant
                            ? t('plant_history.loading')
                            : t('plant_history.load_more')}
                    </Button>
                </ListItem>
            </List>
            <div style={{ height: 240, width: '100%' }}>
                    {plotData ? (
                        <ReactEChartsCore
                            key={`${selectedLayer.id}`}
                            ref={eChartsRef}
                            echarts={echarts}
                            style={{ height: 450 }}
                            option={{
                            legend: {
                                data: ['Max', 'Min', 'Mean'],
                                top: 25, left:0,
                                selected: {
                                    Max: false,
                                    Min: false,
                                    Mean: true,
                                }
                            },
                            dataset: plotData,
                            xAxis: {
                                type: 'category',
                                data: plotData.dates,
                            },
                            yAxis: {
                                type: 'value',
                            },
                            series: [
                              {
                                name: 'Max',
                                type: 'line',
                                data: plotData.maxValues,
                                smooth: true,
                                lineStyle: { color: 'red' },
                                itemStyle: { color: 'red' },
                              },
                              {
                                name: 'Min',
                                type: 'line',
                                data: plotData.minValues,
                                smooth: true,
                                lineStyle: { color: 'blue' },
                                itemStyle: { color: 'blue' },
                              },
                              {
                                name: 'Mean',
                                type: 'line',
                                data: plotData.meanValues,
                                smooth: true,
                                lineStyle: { color: 'green' },
                                itemStyle: { color: 'green' },  
                              }
                            ],
                            dataZoom: [
                                {
                                type: 'inside',
                                },
                                {
                                start: 0,
                                end: 20,
                                },
                                
                            ],
                            tooltip: {
                                trigger: 'axis',
                                axisPointer: {
                                type: 'cross',
                                animation: false,
                                label: {
                                    backgroundColor: '#505765',
                                },
                                },
                            },
                            }}
                        />
                    ) : (
                        isLoadingPlant || isFetchingPlant || dataPlant === undefined ? 
                            <CenteredProgress /> 
                        : 
                            <Typography variant="body2" color="textSecondary">{t("Plant no data")}</Typography>
                        )}
            </div>
        </React.Fragment>
    )
}
